import { LaboratoryDTO } from 'src/app/DTOs/LaboratoryDTO';import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MasterDataService } from 'src/app/Services/master-data.service';
import { FormGroup, FormsModule } from "@angular/forms";
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { CountryDTO } from 'src/app/DTOs/CountryDTO';
import { ContactPersonDTO } from 'src/app/DTOs/ContactPersonDTO';

import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from 'src/app/Shared/prime-ng/notification.service';
import { DataResult, DataStateChangeEvent, GridDataResult, PageChangeEvent, State, pageSkip, pageTake, process } from 'src/app/Shared/prime-ng/grid-data';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';

type LaboratoryStatusField = 'isStandard' | 'isSampleLabStandard';

@Component({
  selector: 'app-laboratory-detail',
  standalone: true,
  imports: [PrimeNgModule, FormsModule, CommonModule, RouterModule],
  templateUrl: './laboratory-detail.component.html',
  styleUrl: './laboratory-detail.component.css'
})

export class LaboratoryDetailComponent implements OnInit, OnDestroy {
  laboratory: LaboratoryDTO | null = {
    laboratoryId: 0,
    title: '',
    country: {
      countryId: 0,
      title: '',
      iso2: null,
      iso3: null
    },
    city: '',
    zip: '',
    street: '',
    contactPersons: [],
    isStandard: false,
    isSampleLabStandard: false,
    canBeDeleted: true,
  };
  availableCountrys: CountryDTO[] = [{ countryId: 0, iso2: null, iso3: null, title: '' }]
  public contactPersons: ContactPersonDTO[] = [];
  public currentContactPersons: ContactPersonDTO[] = [];
  public originalContactPerson: ContactPersonDTO[] = [];
  public gridView: GridDataResult = { data: [], total: 0 };
  public gridState: State = { skip: 0, take: 5 };
  selectedCountryId: number | null = null;
  editMode: boolean = false
  cancelDialog: boolean = false
  statusChangeDialog: boolean = false
  pendingStatusField: LaboratoryStatusField | null = null;
  pendingStatusLaboratoryName = '';
  private destroy$ = new Subject<void>();

  constructor(private route: ActivatedRoute, private masterData: MasterDataService, private router: Router, private notificationService: NotificationService) { }

  ngOnInit(): void {
    this.loadLaboratoryDetail()
    this.getAllCountrys()
    this.updateGridView()
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveLaboratory() {
    if (!this.laboratory) {
      this.notification('No Laboratory was loaded!', 'error');
      return;
    }

    const selectedCountry = this.availableCountrys.find(c => c.countryId === this.selectedCountryId);
    if (!selectedCountry) {
      this.notification('Please select a Country!', 'error');
      return;
    }
    this.laboratory.country = selectedCountry;
    this.laboratory.contactPersons = this.contactPersons;

    if (this.laboratory.laboratoryId && this.laboratory.laboratoryId > 0) {
      this.masterData.updateLaboratory(this.laboratory).subscribe({
        next: () => {
          this.notification('Laboratory was updated', 'success');
          this.router.navigate(['/laboratories']);
        },
        error: () => this.notification('Error while updating Laboratory', 'error')
      });
    } else {
      this.masterData.createLaboratory(this.laboratory).subscribe({
        next: (id: number) => {
          this.notification(`Laboratory was created (ID: ${id})`, 'success');
          this.router.navigate(['/laboratories']);
        },
        error: () => this.notification('Error while creating Laboratory', 'error')
      });
    }
  }

  private resetLaboratory(): void {
    this.laboratory = {
      laboratoryId: 0,
      title: '',
      country: this.availableCountrys[0] || {
        countryId: 0,
        title: '',
        iso2: null,
        iso3: null
      },
      city: '',
      zip: '',
      street: '',
      contactPersons: [],
      isStandard: false,
      isSampleLabStandard: false,
      canBeDeleted: true,
    };
    this.contactPersons = [];
    this.originalContactPerson = [];
    this.selectedCountryId = this.laboratory?.country?.countryId || null;
    this.updateGridView();
  }

  cancelEdit() {
    this.laboratory = null;
    this.router.navigate([`laboratories/`])
  }

  getAllCountrys() {
    this.masterData.GetAllCountrys().subscribe({
      next: (res) => {
        if (res) {
          this.availableCountrys = res;
          if (this.laboratory) {
            if (this.laboratory.country && this.laboratory.country.countryId) {
              this.selectedCountryId = this.laboratory.country.countryId;
            } else {
              this.laboratory.country = this.availableCountrys[0];
              this.selectedCountryId = this.laboratory.country.countryId;
            }
          }
        }
      },
      error: () => this.notification('Error while loading countries', 'error')
    });
  }

  private updateGridView() {
    this.gridView = process(this.contactPersons, this.gridState);
  }

  public dataStateChange(state: State): void {
    this.gridState = state;
    this.updateGridView();
  }

  public addHandler() {
    try {
      const newContactPerson: ContactPersonDTO = {
        databaseId: 0,
        firstName: 'First Name',
        lastName: 'Last Name',
        canBeDeleted: true,
      };
      this.contactPersons = [...this.contactPersons, newContactPerson];
      this.updateGridView()
      this.notification('New Contactperson was added', 'success');
    } catch {
      this.notification('Error while saving Contactperson', 'error');
    }
  }

  public editHandler() {
    this.editMode = !this.editMode
    this.currentContactPersons = this.contactPersons.map(cp => ({ ...cp }))
  }

  public openCancelDialog() {
    this.cancelDialog = true;
  }

  public cancelHandler() {
    try {
      this.contactPersons = this.originalContactPerson.map(cp => ({ ...cp }))
      this.toggleCancelDialog()
      this.updateGridView();
      this.notification('Changes were canceled', 'info');
      this.router.navigate(['/laboratories']);
    } catch {
      this.notification('Error while canceling the changes', 'error');
    }
  }

  public saveHandler() {
    this.editMode = !this.editMode
    this.updateGridView();
  }

  public removeHandler(item: ContactPersonDTO) {
    const index = this.contactPersons.indexOf(item);
    if (index > -1) {
      this.contactPersons.splice(index, 1);
      this.updateGridView();
      this.notification(`Contactperson "${item.firstName} ${item.lastName}" was removed`, 'warning');
    }
  }

  private loadLaboratoryDetail() {
    this.resetLaboratory()
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.masterData.getLaboratories(id).subscribe(laboratories => {
        this.laboratory = laboratories[0];
        if (this.laboratory) {
          this.laboratory.laboratoryId = this.laboratory.laboratoryId;
        }
        if (this.laboratory && this.laboratory.country) {
          this.selectedCountryId = this.laboratory.country.countryId
          this.contactPersons = this.laboratory.contactPersons.map(cp => ({ ...cp }));
          this.originalContactPerson = this.contactPersons.map(cp => ({ ...cp }));
          this.updateGridView();
        }
      });
    }
  }

  toggleCancelDialog() {
    this.cancelDialog = !this.cancelDialog
  }

  onStatusToggle(field: LaboratoryStatusField, checked: boolean): void {
    if (!this.laboratory) {
      return;
    }

    if (!checked) {
      this.laboratory[field] = false;
      return;
    }

    this.masterData.getLaboratories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: laboratories => {
          const currentHolder = laboratories.find(laboratory =>
            laboratory.laboratoryId !== this.laboratory?.laboratoryId && laboratory[field]
          );

          if (!currentHolder) {
            this.applyLaboratoryStatus(field);            return;
          }

          this.laboratory![field] = false;
          this.pendingStatusField = field;
          this.pendingStatusLaboratoryName = this.laboratory?.title || 'This laboratory';          this.statusChangeDialog = true;
        },
        error: () => {
          this.laboratory![field] = false;
          this.notification('Error while checking laboratory status', 'error');
        }
      });
  }

  confirmStatusChange(): void {
    if (!this.laboratory || !this.pendingStatusField) {
      this.closeStatusChangeDialog();
      return;
    }

    this.applyLaboratoryStatus(this.pendingStatusField);    this.clearPendingStatusChange();  }

  closeStatusChangeDialog(): void {
    if (this.laboratory && this.pendingStatusField) {
      this.laboratory[this.pendingStatusField] = false;
    }

    this.clearPendingStatusChange();  }  private applyLaboratoryStatus(field: LaboratoryStatusField): void {    if (!this.laboratory) {      return;    }    const otherField: LaboratoryStatusField = field === 'isStandard'      ? 'isSampleLabStandard'      : 'isStandard';    this.laboratory[field] = true;    this.laboratory[otherField] = false;  }  private clearPendingStatusChange(): void {    this.pendingStatusField = null;
    this.pendingStatusLaboratoryName = '';
    this.statusChangeDialog = false;
  }

  get pendingStatusLabel(): string {
    if (this.pendingStatusField === 'isSampleLabStandard') {
      return 'Is Sample Lab Standard';
    }

    return 'Is Standard';
  }

  notification(message: string, state: any) {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: state, icon: true }
    });
  }
}
